import React from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, BarChart, Bar, ComposedChart, Area, AreaChart } from 'recharts';
import { useFinancialStats } from '../../hooks/useFinancialStats';
import { useTreatmentStats } from '../../hooks/useTreatmentStats';
import LoadingSpinner from '../common/LoadingSpinner';
import StatsCard from '../common/StatsCard';
import { formatCurrency } from '../../utils/formatters';

interface Props {
  dateRange: { start_date?: string; end_date?: string };
}

export default function FinancialBillingTab({ dateRange }: Props) {
  const { data: financialStats, isLoading: loadingFinancial } = useFinancialStats(dateRange);
  const { data: treatmentStats, isLoading: loadingTreatments } = useTreatmentStats(dateRange);

  if (loadingFinancial || loadingTreatments) return <LoadingSpinner />;
  if (!financialStats || !treatmentStats) return null;

  // Calculs pour les KPIs
  const totalInvoiced = financialStats.invoiced_by_month.reduce((sum, item) => sum + item.total, 0);
  const averageMonthly = totalInvoiced / (financialStats.invoiced_by_month.length || 1);
  const bestTreatment = treatmentStats.top_treatments[0];
  const avgTreatmentValue = treatmentStats.top_treatments.reduce((sum, t) => sum + t.average_price, 0) / treatmentStats.top_treatments.length;

  // Données pour le graphique combiné revenus/traitements
  const combinedData = financialStats.invoiced_by_month.map(invoice => {
    const treatment = treatmentStats.treatments_by_month.find(t => t.month === invoice.month);
    return {
      month: new Date(invoice.month).toLocaleDateString('fr-FR', { month: 'short', year: 'numeric' }),
      revenue: invoice.total,
      treatments: treatment?.count || 0,
      avg_per_treatment: treatment?.count ? invoice.total / treatment.count : 0
    };
  });

  return (
    <div className="space-y-6">
      {/* KPIs Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <StatsCard
          title="Total Facturé"
          value={formatCurrency(totalInvoiced)}
          icon="💰"
          color="green"
          change={{ value: 12.5, type: 'increase' }}
        />
        <StatsCard
          title="Moyenne Mensuelle"
          value={formatCurrency(averageMonthly)}
          icon="📊"
          color="blue"
        />
        <StatsCard
          title="Créances en Cours"
          value={formatCurrency(financialStats.outstanding_amount)}
          icon="⏱️"
          color="red"
        />
        <StatsCard
          title="Valeur Moy/Traitement"
          value={formatCurrency(avgTreatmentValue)}
          icon="🎯"
          color="purple"
        />
      </div>

      {/* Graphiques principaux */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Évolution du CA */}
        <div className="bg-white p-6 rounded-lg shadow">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Évolution du Chiffre d'Affaires</h3>
          <LineChart width={500} height={300} data={combinedData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="month" />
            <YAxis />
            <Tooltip formatter={(value, name) => [
              name === 'revenue' ? formatCurrency(Number(value)) : value,
              name === 'revenue' ? 'Revenus' : 'Traitements'
            ]} />
            <Legend />
            <Line type="monotone" dataKey="revenue" stroke="#8884d8" strokeWidth={3} name="Revenus" />
          </LineChart>
        </div>

        {/* Top traitements par revenus */}
        <div className="bg-white p-6 rounded-lg shadow">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Top Traitements par Revenus</h3>
          <BarChart width={500} height={300} data={treatmentStats.top_treatments.slice(0, 8)}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" angle={-45} textAnchor="end" height={100} />
            <YAxis />
            <Tooltip formatter={(value) => [formatCurrency(Number(value)), 'Revenus']} />
            <Bar dataKey="revenue" fill="#82ca9d" />
          </BarChart>
        </div>
      </div>

      {/* Analyse revenus vs volume */}
      <div className="bg-white p-6 rounded-lg shadow">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Analyse Revenus vs Volume de Traitements</h3>
        <ComposedChart width={1000} height={400} data={combinedData}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="month" />
          <YAxis yAxisId="left" />
          <YAxis yAxisId="right" orientation="right" />
          <Tooltip formatter={(value, name) => {
            if (name === 'revenue') return [formatCurrency(Number(value)), 'Revenus'];
            if (name === 'treatments') return [value, 'Nb Traitements'];
            return [formatCurrency(Number(value)), 'Moy/Traitement'];
          }} />
          <Legend />
          <Bar yAxisId="left" dataKey="revenue" fill="#8884d8" name="Revenus" />
          <Line yAxisId="right" type="monotone" dataKey="treatments" stroke="#ff7300" strokeWidth={2} name="Nb Traitements" />
          <Line yAxisId="right" type="monotone" dataKey="avg_per_treatment" stroke="#00ff00" strokeWidth={2} name="Moy/Traitement" />
        </ComposedChart>
      </div>

      {/* Statistiques par spécialité */}
      <div className="bg-white p-6 rounded-lg shadow">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Performance par Médecin</h3>
        <BarChart width={1000} height={350} data={treatmentStats.doctor_stats}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="doctor_name" angle={-30} textAnchor="end" height={80} />
          <YAxis />
          <Tooltip formatter={(value, name) => [
            name === 'revenue' ? formatCurrency(Number(value)) : value,
            name === 'revenue' ? 'Revenus' : 'Traitements'
          ]} />
          <Legend />
          <Bar dataKey="revenue" fill="#8884d8" name="Revenus" />
          <Bar dataKey="treatments" fill="#82ca9d" name="Nombre de Traitements" />
        </BarChart>
      </div>
    </div>
  );
}